// Multiple-choice question building, and the arithmetic behind the placement test.
//
// Kept out of the browser code and free of app state so it can be tested directly: the parts that
// can quietly be wrong here are not the rendering but the CHOICES — a distractor that is also a
// correct answer makes a question unanswerable, and a vocabulary estimate that forgets people guess
// flatters every learner who takes the test.

import { readingAdds } from './phonology.js';
import type { Word } from './types.js';

/** How a drill question asks about a word. */
export type DrillKind = 'meaning' | 'hanzi' | 'audio' | 'pinyin' | 'gap';

export interface DrillInfo {
  id: DrillKind;
  name: string;
  blurb: string;
}

export const DRILLS: DrillInfo[] = [
  { id: 'meaning', name: 'Meaning', blurb: 'See the characters, choose what they mean.' },
  { id: 'hanzi', name: 'Characters', blurb: 'See the English, choose the characters.' },
  { id: 'audio', name: 'Listening', blurb: 'Hear the word, choose the characters.' },
  { id: 'pinyin', name: 'Reading', blurb: 'See the characters, choose how they are said.' },
  { id: 'gap', name: 'Gap fill', blurb: 'A word is missing from a sentence — put it back.' },
];

/** Options per question. Four is the usual compromise: guessing is 25%, and all four fit a phone. */
export const OPTIONS = 4;

export interface Question {
  kind: DrillKind;
  /** The word being asked about, as an index into the vocabulary. */
  answer: number;
  /** Every option, the answer among them, already shuffled. */
  options: number[];
  /** For 'gap' only: the sentence with the word taken out of it. */
  sentence?: number;
}

/** A source of randomness, so tests can make question building deterministic. */
export type Rand = () => number;

export function shuffled<T>(items: T[], rand: Rand): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/**
 * Whether two words are too close to be told apart as answer and distractor.
 *
 * Not a nicety. 猫 offered against 小猫, or 高兴 against 高, gives a question with two defensible
 * answers, and a learner who knows the word perfectly well can still get it wrong. Words that share
 * a meaning are the same trap on the other side: "to look" and "to see" as two of four options is a
 * test of the glossary, not of the learner.
 */
export function tooSimilar(a: Word, b: Word): boolean {
  if (a.s.includes(b.s) || b.s.includes(a.s)) return true;
  const ga = senses(a);
  for (const g of senses(b)) if (ga.has(g)) return true;
  return false;
}

/**
 * The individual senses in a word's glosses.
 *
 * Glosses arrive semicolon-joined — 不 is "not; no" and 没 is "not; have not" — so comparing whole
 * strings finds no overlap between two words that both plainly mean "not", and the drill offered
 * them side by side. Splitting first is what makes the comparison mean anything. Parenthesised
 * notes are dropped: "(negative prefix for verbs) have not" is the sense "have not" wearing a label.
 */
function senses(word: Word): Set<string> {
  const out = new Set<string>();
  for (const gloss of word.e) {
    for (const part of gloss.replace(/\([^)]*\)/gu, ' ').split(/[;,]/u)) {
      const sense = part.toLowerCase().replace(/^\s*to /u, '').trim();
      if (sense) out.add(sense);
    }
  }
  return out;
}

/**
 * Wrong options for a question about `answer`.
 *
 * Drawn from words of the SAME PART OF SPEECH and NEARBY FREQUENCY wherever the pool allows. Both
 * matter for the question to measure anything: a verb hidden among three nouns can be picked
 * without reading it, and a Top 100 word against three rare ones is just as obvious. The pool is
 * widened rather than the question abandoned when there are not enough near neighbours.
 */
export function pickDistractors(
  words: Word[],
  pool: number[],
  answer: number,
  rand: Rand,
  count = OPTIONS - 1,
): number[] {
  const target = words[answer];
  if (!target) return [];
  const usable = pool.filter((id) => id !== answer && words[id] && !tooSimilar(target, words[id]!));

  // Nearest in frequency first, then take a random handful from that neighbourhood rather than the
  // very nearest — otherwise the same word is always the distractor for the same answer.
  const rank = (id: number) => Math.abs(words[id]!.r - target.r);
  const samePos = usable.filter((id) => words[id]!.pos === target.pos);
  const tiers = [samePos, usable];

  for (const tier of tiers) {
    if (tier.length < count) continue;
    const near = tier.slice().sort((a, b) => rank(a) - rank(b)).slice(0, Math.max(count * 6, 12));
    return shuffled(near, rand).slice(0, count);
  }
  return shuffled(usable, rand).slice(0, count);
}

/**
 * Can this word be ASKED about in this drill, or does the question contain its own answer?
 *
 * The Reading drill shows the word and offers four readings, so a word that reads as itself is a
 * card whose prompt is already the answer: は against は, and the learner picks their own prompt
 * out of a list. 582 of the 2,092 Japanese words are like that — 27.8%, including all 92 letters
 * the alphabet decks teach — so better than a quarter of that drill was free marks. Mandarin never
 * showed it: zero of its 6,639 words read as themselves (MYN-203).
 *
 * Every other drill asks something the prompt does not contain, so this is the one kind that has
 * to check.
 */
export function askable(word: Word, kind: DrillKind): boolean {
  return kind !== 'pinyin' || readingAdds(word.s, word.p);
}

/** Build one question, or null when the pool cannot supply enough distinct options. */
export function buildQuestion(
  words: Word[],
  pool: number[],
  answer: number,
  kind: DrillKind,
  rand: Rand,
): Question | null {
  const target = words[answer];
  if (!target || !askable(target, kind)) return null;
  // A wrong option that cannot be READ apart from the answer is the same fault from the other
  // side, so the distractors are drawn from askable words only.
  const askablePool = kind === 'pinyin' ? pool.filter((id) => words[id] && askable(words[id]!, kind)) : pool;
  const distractors = pickDistractors(words, askablePool, answer, rand);
  if (distractors.length < OPTIONS - 1) return null;
  // AND NO TWO OPTIONS MAY READ ALIKE. tooSimilar compares spellings and meanings, which is right
  // for every other drill and blind to this one: Japanese is full of homophones, and 橋 offered
  // against 箸 in a Reading drill is two correct answers wearing different characters.
  if (kind === 'pinyin') {
    const said = new Set([target.p]);
    for (const id of distractors) {
      const p = words[id]!.p;
      if (said.has(p)) return null;
      said.add(p);
    }
  }
  return { kind, answer, options: shuffled([answer, ...distractors], rand) };
}

// ---------------------------------------------------------------- the placement test

export interface Band {
  /** Inclusive frequency-rank bounds. */
  from: number;
  to: number;
  label: string;
}

/**
 * The frequency bands the test samples.
 *
 * Widening as they go up, because that is how vocabulary knowledge thins out: the difference
 * between knowing 50 and 100 of the commonest words is a beginner's whole world, while between
 * 3000 and 3500 it is a rounding error. Equal-width bands would spend most of the test measuring
 * the part that matters least.
 */
export const BANDS: Band[] = [
  { from: 1, to: 100, label: 'Top 100' },
  { from: 101, to: 300, label: '101–300' },
  { from: 301, to: 600, label: '301–600' },
  { from: 601, to: 1200, label: '601–1200' },
  { from: 1201, to: 2500, label: '1201–2500' },
  { from: 2501, to: 5000, label: '2501–5000' },
];

export interface BandResult {
  from: number;
  to: number;
  asked: number;
  right: number;
}

/** Questions asked per band. Six bands at four each is 24 questions — a few minutes, not an exam. */
export const PER_BAND = 4;

/**
 * Two bands in a row at or below chance ends the test.
 *
 * Bands only get harder, so once a learner has scored no better than guessing twice over there is
 * no reason to believe the bands above will go better, and asking twelve more questions nobody can
 * answer is a good way to make someone stop taking the test.
 */
export const GIVE_UP_AFTER = 2;

/**
 * Whether a band told us nothing.
 *
 * At or below chance, not exactly zero. With four options a band of four questions yields one right
 * answer by luck alone, so "zero right" almost never happens and the early stop almost never fired:
 * a beginner who guessed one correctly in the 301-600 band was asked twelve more questions from
 * bands far beyond them. One lucky guess is not evidence of knowledge, and the rule should not
 * treat it as such.
 */
export function learnedNothing(band: BandResult, options = OPTIONS): boolean {
  return band.asked > 0 && band.right <= band.asked / options;
}

export interface TestEstimate {
  /** Estimated words known within the ranks the test covers. */
  known: number;
  /** The highest band the learner was still mostly right in, or null if none. */
  reachedTo: number | null;
  /** Per-band share known, after correcting for guessing. */
  bands: { from: number; to: number; share: number; asked: number; right: number }[];
}

/**
 * Turn band scores into an estimated vocabulary size.
 *
 * The correction for guessing is the part that earns its keep. With four options, someone who knows
 * none of the words still scores about 25%, so raw accuracy would credit a complete beginner with
 * a quarter of every band — around 1250 words they cannot read. The standard correction assumes a
 * wrong answer means a genuine gap and a right one is either knowledge or a lucky guess among the
 * options not known: p_true = (p_observed - 1/n) / (1 - 1/n), floored at zero.
 *
 * Bands the test never reached count as zero rather than being extrapolated. The test stops when a
 * learner is missing everything, and guessing upward from that is how an estimate becomes fiction.
 *
 * AND NEITHER IS A BAND EXTRAPOLATED PAST THE END OF THE COURSE. `reach` is the highest frequency
 * rank the course actually has a word for, and a band is credited only as far as that. Without it
 * a perfect run of the Japanese test reported 2,500 words known against a course of 2,092 whose
 * highest rank IS 2,092 — 408 words credited at ranks where nothing exists. en-ja and en-zh
 * invented 500 apiece (MYN-204).
 *
 * The two kinds of rank are why this is a parameter and not a constant. In zh-en, `r` is a
 * position in a 42,376-word frequency list, so the ranks between the words the course teaches are
 * real words of the language and estimating across the whole band is sound — it is an estimate of
 * Mandarin, not of the deck. In the other three courses `r` is just the position in that course's
 * own list, so a rank above its last word is not a word anywhere.
 */
export function estimate(results: BandResult[], options = OPTIONS, reach = Infinity): TestEstimate {
  const chance = 1 / options;
  let known = 0;
  let reachedTo: number | null = null;
  const bands = BANDS.map((band) => {
    const got = results.find((r) => r.from === band.from);
    const asked = got?.asked ?? 0;
    const right = got?.right ?? 0;
    const observed = asked > 0 ? right / asked : 0;
    const share = asked > 0 ? Math.max(0, (observed - chance) / (1 - chance)) : 0;
    // Bands are inclusive at both ends, so 1..100 is 100 words, not 99 — and stop at the last
    // rank the course reaches, which for three of the four courses is inside the top band asked.
    const width = Math.max(0, Math.min(band.to, reach) - band.from + 1);
    known += share * width;
    if (share >= 0.5) reachedTo = band.to;
    return { from: band.from, to: band.to, share, asked, right };
  });
  return { known: Math.round(known), reachedTo, bands };
}

/**
 * Which deck to start in, given an estimate.
 *
 * Deliberately one step BELOW where the learner tested out. A deck is a queue of things you do not
 * know yet, and starting it at the exact edge of your ability means every card is new at once,
 * which is the fastest way to abandon it.
 */
export function suggestedDeck(known: number): string {
  if (known < 80) return 'top-100';
  if (known < 400) return 'top-500';
  return 'top-1000';
}
